import { QueryClientProvider } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { authService } from '@/features/auth/services/authService';
import { tokenService } from '@/features/auth/services/tokenService';
import { restoreSession, sessionExpired } from '@/features/auth/store/authSlice';
import { registerAuthTokenProvider } from '@/shared/utils/api/authTokenProvider';
import { store } from '../store';
import { createQueryClient } from './queryClient';
import { UserPreferencesProvider } from './UserPreferencesProvider';

/**
 * Connects the auth feature to the transport layer and recovers any persisted session.
 *
 * Registration happens in a layout effect-like ref guard rather than at module scope so
 * that tests can mount the tree repeatedly against a fresh store.
 */
function AuthBootstrap({
  children,
  queryClient,
}: {
  readonly children: ReactNode;
  readonly queryClient: QueryClient;
}): React.JSX.Element {
  const hasBootstrapped = useRef(false);

  useEffect(() => {
    if (hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    registerAuthTokenProvider({
      getAccessToken: () => tokenService.getAccessToken(),
      refreshAccessToken: () => authService.refreshAccessToken(),
      onSessionExpired: () => {
        tokenService.clear();
        queryClient.clear();
        store.dispatch(sessionExpired());
      },
    });

    void store.dispatch(restoreSession());
  }, [queryClient]);

  return <>{children}</>;
}

export interface AppProvidersProps {
  readonly children: ReactNode;
}

/**
 * Composition root for every cross-cutting provider the app depends on.
 *
 * The theme provider is nested inside the store and the query client, not wrapped around
 * them: which palette to render is the user's saved preference, so it cannot be decided
 * until there is a session to read that preference from.
 */
export function AppProviders({ children }: AppProvidersProps): React.JSX.Element {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <AuthBootstrap queryClient={queryClient}>
          <UserPreferencesProvider>
            <BrowserRouter>{children}</BrowserRouter>
          </UserPreferencesProvider>
        </AuthBootstrap>
      </QueryClientProvider>
    </ReduxProvider>
  );
}
